/**
 * Shared list header and breadcrumb rendering for bookmarks and tribes.
 * Extracts the identical markup structure; list-specific dropdown items
 * and breadcrumb root labels are passed as parameters.
 */

import { escapeHtml } from '../helpers/escapeHtml';
import { _ } from '../i18n';

export interface DropdownItem {
  action: string; // data-action value, e.g. 'new-folder'
  icon: string; // SVG string
  label: string; // display text, e.g. 'Folder'
}

/**
 * Render the list header with title and "New" dropdown button.
 * `headerActionsHtml` (optional): extra actions rendered LEFT of the
 * "+ New" dropdown, grouped with it on the right side (e.g. the
 * bookmarks "Mark as read" bulk button).
 */
export function renderListHeader(
  title: string,
  dropdownItems: DropdownItem[],
  headerActionsHtml: string = ''
): string {
  const itemsHtml = dropdownItems
    .map(
      item => `
    <li class="nn-dropdown__item" data-action="${item.action}">
      ${item.icon}
      ${escapeHtml(item.label)}
    </li>
  `
    )
    .join('');

  const dropdownBlock = `
      <div class="nn-dropdown" data-list-header-dropdown>
        <button class="nn-dropdown__trigger" type="button" title="${_('Create new')}">
          <span class="nn-dropdown__label">${_('+ New')}</span>
          <span class="nn-dropdown__arrow" aria-hidden="true"></span>
        </button>
        <ul class="nn-dropdown__menu" role="listbox">
          ${itemsHtml}
        </ul>
      </div>`;

  const rightBlock = headerActionsHtml
    ? `<div class="bookmark-header-actions">${headerActionsHtml}${dropdownBlock}</div>`
    : dropdownBlock;

  return `
    <div class="l-spread">
      <h2>${escapeHtml(title)}</h2>
      ${rightBlock}
    </div>
  `;
}

/**
 * Render breadcrumb navigation for folder views.
 */
export function renderListBreadcrumb(
  rootLabel: string,
  folderName: string
): string {
  return `
    <div class="bookmark-breadcrumb">
      <span class="bookmark-breadcrumb__item" data-navigate="root">${escapeHtml(rootLabel)}</span>
      <span class="bookmark-breadcrumb__separator">/</span>
      <span class="bookmark-breadcrumb__item bookmark-breadcrumb__item--current">${escapeHtml(folderName)}</span>
    </div>
  `;
}

/**
 * Bind the header dropdown toggle and outside-click close handler.
 * Returns a cleanup function to remove the document listener.
 */
export function bindHeaderDropdown(
  container: HTMLElement,
  closeHandler: { current: ((e: Event) => void) | null }
): void {
  const dropdown = container.querySelector('[data-list-header-dropdown]');
  const trigger = dropdown?.querySelector('.nn-dropdown__trigger');

  trigger?.addEventListener('click', e => {
    e.stopPropagation();
    dropdown?.classList.toggle('nn-dropdown--open');
  });

  if (closeHandler.current) {
    document.removeEventListener('click', closeHandler.current);
  }

  closeHandler.current = (e: Event) => {
    if (!dropdown?.contains(e.target as Node)) {
      dropdown?.classList.remove('nn-dropdown--open');
    }
  };
  document.addEventListener('click', closeHandler.current);
}
